<template>
  <!-- ==================== Hero Panel — TODAY 4메트릭 (Figma 4006:289) ==================== -->
  <div :class="['hero-panel', { 'hero-panel--mobile': isMobile }]">
    <header class="hero-panel__header">
      <span class="hero-panel__live">
        <span class="hero-panel__live-dot"></span>
        {{ t('dashboard.hero.todayLive') }}
      </span>
      <span class="hero-panel__time">{{ formattedTime }}</span>
    </header>

    <div class="hero-panel__metrics">
      <div
        v-for="(metric, idx) in metrics"
        :key="metric.key"
        :class="['hero-metric', `hero-metric--${metric.key}`]"
      >
        <div class="hero-metric__value">{{ formatNumber(animatedValues[metric.key]) }}</div>
        <div class="hero-metric__label">{{ t(metric.labelKey) }}</div>
        <div class="hero-metric__sub">{{ subText(metric.key) }}</div>
        <span v-if="idx < metrics.length - 1" class="hero-metric__divider" aria-hidden="true"></span>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps */
import { computed, reactive, watch, onUnmounted } from 'vue'
import i18n from '@/locales/i18n'

const t = i18n.global.t

const ANIM_DURATION_MS = 600

const props = defineProps({
  /**
   * @type {{
   *   fetchedAt?: string,
   *   receipt?: number,
   *   complete?: number,
   *   inProgress?: number,
   *   unassigned?: number,
   *   slaImminent?: number
   * }}
   */
  summary: {
    type: Object,
    default: () => ({
      receipt: 0,
      complete: 0,
      inProgress: 0,
      unassigned: 0,
      slaImminent: 0
    })
  },
  isMobile: { type: Boolean, default: false }
})

const metrics = [
  { key: 'receipt', labelKey: 'dashboard.hero.receipt' },
  { key: 'complete', labelKey: 'dashboard.hero.complete' },
  { key: 'inProgress', labelKey: 'dashboard.hero.inProgress' },
  { key: 'unassigned', labelKey: 'dashboard.hero.unassigned' }
]

const animatedValues = reactive({
  receipt: 0,
  complete: 0,
  inProgress: 0,
  unassigned: 0
})

let rafId = null

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3)
}

function animateMetrics(from, to) {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  const startTime = performance.now()
  function step(now) {
    const elapsed = now - startTime
    const progress = Math.min(elapsed / ANIM_DURATION_MS, 1)
    const eased = easeOutCubic(progress)
    for (const m of metrics) {
      const f = Number(from[m.key] || 0)
      const tt = Number(to[m.key] || 0)
      animatedValues[m.key] = f + (tt - f) * eased
    }
    if (progress < 1) {
      rafId = requestAnimationFrame(step)
    } else {
      rafId = null
      for (const m of metrics) {
        animatedValues[m.key] = Number(to[m.key] || 0)
      }
    }
  }
  rafId = requestAnimationFrame(step)
}

watch(
  () => props.summary,
  (next, prev) => {
    const from = {
      receipt: prev?.receipt ?? animatedValues.receipt,
      complete: prev?.complete ?? animatedValues.complete,
      inProgress: prev?.inProgress ?? animatedValues.inProgress,
      unassigned: prev?.unassigned ?? animatedValues.unassigned
    }
    const to = next || {}
    animateMetrics(from, to)
  },
  { deep: true, immediate: true }
)

onUnmounted(() => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
})

function formatNumber(value) {
  const num = Math.round(Number(value) || 0)
  return num.toLocaleString()
}

/** Figma 시안의 메트릭별 sub-text 생성. 데이터 부재 시 빈 문자열로 폴백. */
function subText(key) {
  const s = props.summary || {}
  const receipt = Number(s.receipt) || 0
  const complete = Number(s.complete) || 0
  const slaImminent = Number(s.slaImminent) || 0
  if (key === 'complete') {
    if (receipt <= 0) return ''
    const rate = Math.round((complete / receipt) * 1000) / 10
    return t('dashboard.hero.completionRate', { rate: rate.toFixed(1) })
  }
  if (key === 'unassigned') {
    if (slaImminent <= 0) return ''
    return t('dashboard.hero.slaImminentCount', { n: slaImminent })
  }
  if (key === 'inProgress') {
    return t('dashboard.hero.assignedDone')
  }
  return ''
}

const formattedTime = computed(() => {
  if (!props.summary?.fetchedAt) return ''
  try {
    const d = new Date(props.summary.fetchedAt)
    if (Number.isNaN(d.getTime())) return ''
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    const ss = String(d.getSeconds()).padStart(2, '0')
    return t('dashboard.hero.refreshedAt', { time: `${hh}:${mm}:${ss}` })
  } catch (e) {
    return ''
  }
})
</script>

<style scoped>
/* ========================================
   DashboardHeroPanel — Figma 4006:289 (1589×134 strip)
   ======================================== */
.hero-panel {
  background: linear-gradient(135deg, #2C4578 0%, #3B5998 100%);
  color: var(--color-white, #fff);
  border-radius: var(--radius-xl, 16px);
  padding: 22px 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: relative;
  overflow: hidden;
}

.hero-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.hero-panel__live {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #fff;
}

.hero-panel__live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ADE80;
  box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7);
  animation: hero-live-pulse 2s infinite;
}

@keyframes hero-live-pulse {
  0% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(74, 222, 128, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
}

.hero-panel__time {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  font-feature-settings: 'tnum';
}

/* ===== Metrics — 4 columns, no card boxes ===== */
.hero-panel__metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  position: relative;
  z-index: 1;
}

.hero-metric {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4px 16px;
}

.hero-metric__value {
  font-size: 32px;
  font-weight: 700;
  color: #fff;
  font-feature-settings: 'tnum';
  line-height: 1.05;
  letter-spacing: -0.01em;
}

.hero-metric__label {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.hero-metric__sub {
  margin-top: 4px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  font-feature-settings: 'tnum';
  min-height: 14px;
}

/* 미배정 카드: 시안 의도 — 항상 빨강 강조 (운영 우선 환기) */
.hero-metric--unassigned .hero-metric__value {
  color: #FF7A6E;
}
.hero-metric--unassigned .hero-metric__sub {
  color: #FFB4AC;
}

/* 컬럼 간 세로 디바이더 */
.hero-metric__divider {
  position: absolute;
  right: 0;
  top: 12%;
  bottom: 12%;
  width: 1px;
  background: rgba(255, 255, 255, 0.15);
}

/* ===== Mobile (<768px) ===== */
@media (max-width: 767px) {
  .hero-panel {
    padding: 16px 18px;
    gap: 12px;
  }
  .hero-panel__metrics {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px 0;
  }
  .hero-metric {
    padding: 4px 8px;
  }
  .hero-metric__value {
    font-size: 24px;
  }
  .hero-metric__label {
    font-size: 12px;
  }
  /* 모바일에서는 2열 레이아웃이라 디바이더 숨김 */
  .hero-metric__divider {
    display: none;
  }
}

/* ===== Tablet (768-1279px) ===== */
@media (min-width: 768px) and (max-width: 1279px) {
  .hero-panel__metrics {
    grid-template-columns: repeat(4, 1fr);
  }
  .hero-metric__value {
    font-size: 28px;
  }
}
</style>
